import { useDebounce } from "@redotech/react-util/debounce";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import { useTranslation } from "@redotech/redo-i18n";
import { BadgeRedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoTagsDropdownInputButton } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-tags-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { SearchableTagDropdown } from "@redotech/redo-web/searchable-tag-dropdown";
import { filterTruthy } from "@redotech/util/array";
import { memo, useMemo, useState } from "react";

export const ListMatchValuesInput = memo(function ListMatchValuesInput({
  matchValues,
  onMatchValuesChange,
  tagInputPlaceholder,
  addTagText,
  optionsFetcher,
  preserveCommas,
}: {
  matchValues?: string[];
  onMatchValuesChange: (matchValues: string[]) => void;
  tagInputPlaceholder: string;
  addTagText?: string;
  optionsFetcher: ({ searchText }: { searchText: string }) => Promise<string[]>;
  preserveCommas?: boolean;
}) {
  const { t } = useTranslation();
  const onRemoveTag = useHandler((value: string | undefined) => {
    const newMatchValues = matchValues?.filter((v) => v !== value) || [];
    onMatchValuesChange(newMatchValues);
  });

  const [dropdownOpen, setDropdownOpen] = useState(false);

  const onAddTag = useHandler((value: string, shouldSplit = false) => {
    const extractedValues = shouldSplit
      ? extractValues(value, preserveCommas)
      : [value.trim()].filter((v) => v.length > 0);

    if (extractedValues.length === 0) {
      return;
    }

    const existingValues = matchValues || [];
    const newValues: string[] = [];
    const duplicateValues: string[] = [];

    for (const sku of extractedValues) {
      if (existingValues.includes(sku)) {
        duplicateValues.push(sku);
      } else {
        newValues.push(sku);
      }
    }

    if (newValues.length > 0) {
      const newMatchValues = [...existingValues, ...newValues];
      onMatchValuesChange(newMatchValues);
    }
  });

  const [dropdownAnchor, setDropdownAnchor] = useState<HTMLElement | null>(
    null,
  );

  const onTagClick = useHandler((value: string, tagWasSelected: boolean) => {
    if (tagWasSelected) {
      onRemoveTag(value.toLowerCase());
    } else {
      onAddTag(value.toLowerCase(), false);
    }
  });

  const onPasteHandler = useHandler((value: string) => {
    onAddTag(value.toLowerCase(), true);
  });

  const [searchText, setSearchText] = useState<string>("");
  const debouncedSearch = useDebounce(searchText, 300);

  const optionsLoad = useLoad(
    async () => await optionsFetcher({ searchText }),
    [debouncedSearch],
  );

  const availableTags = useMemo(
    () => optionsLoad?.value?.map((v) => v.toLowerCase()) || [],
    [optionsLoad?.value],
  );

  const selectedTags = useMemo(
    () => (matchValues?.filter((v) => v) as string[]) || [],
    [matchValues],
  );

  const selectedItems: BadgeRedoListItem<string>[] = useMemo(
    () =>
      (matchValues || []).map((tag) => ({
        badge: { color: "gray", text: tag },
        value: tag,
        type: "badge" as const,
        id: tag || "",
      })),
    [matchValues],
  );

  const handleRemoveItem = useHandler((item: { value: unknown }) =>
    onRemoveTag(item.value as string),
  );

  const toggleDropdown = useHandler(() => setDropdownOpen((prev) => !prev));

  const handleCloseDropdown = useHandler(() => setDropdownOpen(false));

  return (
    <Flex gap="3xl" maxW="full" w="full">
      <Flex
        maxHeight="half-screen"
        maxW="full"
        ref={setDropdownAnchor}
        w="full"
        wrap="wrap"
      >
        <RedoTagsDropdownInputButton
          isDropdownOpen={dropdownOpen}
          onClick={toggleDropdown}
          placeholder={t("Select values")}
          removeItem={handleRemoveItem}
          selectedItems={selectedItems}
          size="xs"
        />
      </Flex>
      <SearchableTagDropdown
        allowAdd={!!addTagText}
        availableTags={availableTags}
        dropdownAnchor={dropdownAnchor}
        dropdownOpen={dropdownOpen}
        newTagText={addTagText ?? ""}
        onCloseDropdown={handleCloseDropdown}
        onPaste={onPasteHandler}
        onTagClick={onTagClick}
        placement="left-start"
        searchText={searchText}
        selectedTags={selectedTags}
        setSearchText={setSearchText}
        tagInputPlaceholder={tagInputPlaceholder}
      />
    </Flex>
  );
});

function extractValues(text: string, preserveCommas?: boolean): string[] {
  return filterTruthy(
    text
      .split(preserveCommas ? /\n+/ : /[,\n]+/)
      .map((value) => value.trim())
      .filter((value) => value.length > 0),
  );
}
